import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { getUnreadCount } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/books", label: "Books" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Journal" },
  { to: "/community", label: "Community" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { isAdmin } = useAdmin();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (isAdmin) getUnreadCount().then((d) => setUnread(d.count)).catch(() => {});
  }, [isAdmin, location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-display text-2xl font-bold tracking-tight text-zinc-50">
          A.R.<span className="text-amber-500"> Winfree</span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-accent uppercase transition-colors ${
                  isActive ? "text-amber-500" : "text-zinc-400 hover:text-zinc-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin/dashboard"
              data-testid="nav-link-dashboard"
              className={({ isActive }) =>
                `relative inline-flex items-center gap-1.5 text-sm tracking-accent uppercase transition-colors ${
                  isActive ? "text-amber-500" : "text-zinc-400 hover:text-zinc-50"
                }`
              }
            >
              <LayoutDashboard size={15} /> Admin
              {unread > 0 && (
                <span data-testid="nav-unread-badge" className="ml-1 bg-amber-500 text-zinc-950 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unread}</span>
              )}
            </NavLink>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-zinc-50"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800">
          <div className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base tracking-accent uppercase ${isActive ? "text-amber-500" : "text-zinc-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
